import { Box, Skeleton, SxProps, Theme, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";

const getImageCacheKey = (url: string): string => {
  try {
    const parsed = new URL(url);

    return parsed.origin + parsed.pathname;
  } catch {
    return url;
  }
};

// Module-level blob URL cache (survives component remounts / page navigation).
// Once an image is fetched we store it as a blob: URL so subsequent mounts
// display it instantly with zero network requests.
const blobUrlCache = new Map<string, string>();
const pendingFetches = new Map<string, Promise<string | null>>();

export const clearCompanyLogoCache = (): void => {
  blobUrlCache.clear();
  pendingFetches.clear();
};

const fetchBlobUrl = (src: string): Promise<string | null> => {
  const key = getImageCacheKey(src);

  const cached = blobUrlCache.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = pendingFetches.get(key);
  if (pending) return pending;

  const promise = fetch(src)
    .then((res) => (res.ok ? res.blob() : null))
    .then((blob) => {
      pendingFetches.delete(key);
      if (!blob) return null;
      const blobUrl = URL.createObjectURL(blob);
      blobUrlCache.set(key, blobUrl);

      return blobUrl;
    })
    .catch(() => {
      pendingFetches.delete(key);

      return null;
    });

  pendingFetches.set(key, promise);

  return promise;
};

interface CompanyLogoProps {
  organizationLogoUrl?: string;
  organizationName: string;
  initials: string;
  width?: number | string;
  height?: number | string;
  aspectRatio?: string;
  maxWidth?: number | string;
  isMobile?: boolean;
}

export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  organizationLogoUrl,
  organizationName,
  initials,
  width,
  height,
  aspectRatio,
  maxWidth,
  isMobile = false,
}) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  const [blobSrc, setBlobSrc] = useState<string | undefined>(() =>
    organizationLogoUrl
      ? blobUrlCache.get(getImageCacheKey(organizationLogoUrl))
      : undefined,
  );
  const [imageLoaded, setImageLoaded] = useState(() => !!blobSrc);

  useEffect(() => {
    if (!organizationLogoUrl) return;

    const key = getImageCacheKey(organizationLogoUrl);

    const cached = blobUrlCache.get(key);
    if (cached) {
      setBlobSrc(cached);

      return;
    }

    fetchBlobUrl(organizationLogoUrl)
      .then((url) => {
        if (url) {
          setBlobSrc(url);
        } else {
          setImageError(true);
        }
      })
      .catch(() => setImageError(true));
  }, [organizationLogoUrl]);

  const showImage = Boolean(blobSrc) && !imageError;

  const getFontSize = () => {
    if (showImage) return 14;

    return isMobile ? 48 : 64;
  };

  const handleImageLoad = () => setImageLoaded(true);

  const logoContainerSx: SxProps<Theme> = {
    borderRadius: "16px",
    bgcolor: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.1)",
    fontWeight: showImage ? 400 : 800,
    fontSize: getFontSize(),
    color: theme.colors.primary[5],
    overflow: "hidden",
    width,
    height,
    aspectRatio,
    maxWidth,
    position: "relative",
  };

  return (
    <Box sx={logoContainerSx}>
      {/* Loading Skeleton */}
      {showImage && !imageLoaded && (
        <Skeleton
          animation="wave"
          height="100%"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "16px",
          }}
          variant="rectangular"
          width="100%"
        />
      )}

      {/* Image */}
      {showImage ? (
        <img
          alt={`${organizationName} logo`}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
          onLoad={handleImageLoad}
          src={blobSrc}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            padding: "16px",
            opacity: imageLoaded ? 1 : 0,
            transition: imageLoaded ? "none" : "opacity 0.4s ease-in-out",
          }}
        />
      ) : (
        // Fallback
        initials || "ORG"
      )}
    </Box>
  );
};
